import { EventStatus, EventType } from "@sitescene/types";

import type { EventsState } from "@/context/events";

import {
	filterValidIds,
	isAllFilterIdsSelected,
	type LocationZoneSensorIdSets,
} from "@/features/events/utils/filter-valid-ids";
import {
	applySeverityToSearchParams,
	parseSeverityFromUrl,
} from "@/features/events/utils/severity-filter-utils";
import {
	dateValueToCalendarDateString,
	isDateRangeInverted,
	parseCalendarDateRangeBound,
} from "@/utils/date-time";

/** Query param keys for event filter URL sync (Dashboard + All Activity). */
export const FILTER_URL_PARAMS = {
	/** CalendarDateTime string, e.g. `2024-06-15T17:00:00`. */
	DATE_TIME_END: "dateTimeEnd",
	/** CalendarDateTime string, e.g. `2024-06-01T09:00:00`. */
	DATE_TIME_START: "dateTimeStart",
	LOCATIONS: "locations",
	SEARCH: "search",
	SENSORS: "sensors",
	SEVERITY: "severity",
	STATUS: "status",
	TYPES: "types",
	ZONES: "zones",
} as const;

/** Pre-datetime URL keys — deleted on write so stale share links do not linger. */
const LEGACY_DATE_TIME_URL_PARAMS = [
	"dateEnd",
	"dateStart",
	"timeEnd",
	"timeStart",
] as const;

export type FilterUrlSyncOptions = {
	/** When true, read/write alert status (All Activity). Dashboard omits `status`. */
	includeAlertStatus?: boolean;
};

export type UrlSyncedFilterState = Pick<
	EventsState,
	| "dateRangeFilter"
	| "eventTypesFilter"
	| "locationsFilter"
	| "searchQueryFilter"
	| "sensorsFilter"
	| "severityFilter"
	| "statusFilter"
	| "zonesFilter"
>;

const isEventType = (value: string): value is EventType =>
	Object.values(EventType).includes(value as EventType);

const isEventStatus = (value: string): value is EventStatus =>
	Object.values(EventStatus).includes(value as EventStatus);

const parseCommaSeparated = (value: null | string): string[] =>
	value
		? value
				.split(",")
				.map((part) => part.trim())
				.filter(Boolean)
		: [];

const joinCommaSeparated = (values: readonly string[]): string =>
	[...values].sort().join(",");

/** Writes filter state into URL search params, omitting default/empty values. */
export function applyFiltersToSearchParams(
	params: URLSearchParams,
	filters: UrlSyncedFilterState,
	idSets: LocationZoneSensorIdSets,
	options: FilterUrlSyncOptions = {},
): void {
	const { includeAlertStatus = false } = options;
	const { locationIds, sensorIds, zoneIds } = idSets;
	const {
		dateRangeFilter,
		eventTypesFilter,
		locationsFilter,
		searchQueryFilter,
		sensorsFilter,
		severityFilter,
		statusFilter,
		zonesFilter,
	} = filters;

	applySeverityToSearchParams(
		params,
		FILTER_URL_PARAMS.SEVERITY,
		severityFilter,
	);

	const trimmedSearch = searchQueryFilter.trim();
	if (trimmedSearch) {
		params.set(FILTER_URL_PARAMS.SEARCH, trimmedSearch);
	} else {
		params.delete(FILTER_URL_PARAMS.SEARCH);
	}

	if (dateRangeFilter) {
		params.set(
			FILTER_URL_PARAMS.DATE_TIME_START,
			dateValueToCalendarDateString(dateRangeFilter.start),
		);
		params.set(
			FILTER_URL_PARAMS.DATE_TIME_END,
			dateValueToCalendarDateString(dateRangeFilter.end),
		);
	} else {
		params.delete(FILTER_URL_PARAMS.DATE_TIME_START);
		params.delete(FILTER_URL_PARAMS.DATE_TIME_END);
	}

	for (const key of LEGACY_DATE_TIME_URL_PARAMS) {
		params.delete(key);
	}

	if (eventTypesFilter.length > 0) {
		params.set(FILTER_URL_PARAMS.TYPES, joinCommaSeparated(eventTypesFilter));
	} else {
		params.delete(FILTER_URL_PARAMS.TYPES);
	}

	if (
		locationsFilter.length > 0 &&
		!isAllFilterIdsSelected(locationsFilter, locationIds)
	) {
		params.set(
			FILTER_URL_PARAMS.LOCATIONS,
			joinCommaSeparated(locationsFilter),
		);
	} else {
		params.delete(FILTER_URL_PARAMS.LOCATIONS);
	}

	if (zonesFilter.length > 0 && !isAllFilterIdsSelected(zonesFilter, zoneIds)) {
		params.set(FILTER_URL_PARAMS.ZONES, joinCommaSeparated(zonesFilter));
	} else {
		params.delete(FILTER_URL_PARAMS.ZONES);
	}

	if (
		sensorsFilter.length > 0 &&
		!isAllFilterIdsSelected(sensorsFilter, sensorIds)
	) {
		params.set(FILTER_URL_PARAMS.SENSORS, joinCommaSeparated(sensorsFilter));
	} else {
		params.delete(FILTER_URL_PARAMS.SENSORS);
	}

	if (includeAlertStatus && statusFilter) {
		params.set(FILTER_URL_PARAMS.STATUS, statusFilter);
	} else {
		params.delete(FILTER_URL_PARAMS.STATUS);
	}
}

/** Keeps only location IDs that exist in the org's current location list. */
export function filterValidLocationIds(
	locationIds: string[],
	validLocationIds: readonly string[],
): string[] {
	return filterValidIds(locationIds, validLocationIds);
}

/**
 * Reads filter fields present in the URL. Only keys with an explicit query
 * param are included so omitted params do not overwrite context defaults.
 */
export function parseUrlFilterUpdates(
	searchParams: URLSearchParams,
	options?: FilterUrlSyncOptions & {
		validIdSets?: LocationZoneSensorIdSets;
	},
): Partial<UrlSyncedFilterState> {
	const { includeAlertStatus = false, validIdSets } = options ?? {};
	const updates: Partial<UrlSyncedFilterState> = {};
	const { locationIds, sensorIds, zoneIds } = validIdSets ?? {
		locationIds: [],
		sensorIds: [],
		zoneIds: [],
	};
	const validateTreeIds = Boolean(validIdSets);

	if (searchParams.has(FILTER_URL_PARAMS.SEVERITY)) {
		updates.severityFilter = parseSeverityFromUrl(
			searchParams.get(FILTER_URL_PARAMS.SEVERITY),
		);
	}

	if (searchParams.has(FILTER_URL_PARAMS.SEARCH)) {
		updates.searchQueryFilter =
			searchParams.get(FILTER_URL_PARAMS.SEARCH) ?? "";
	}

	if (
		searchParams.has(FILTER_URL_PARAMS.DATE_TIME_START) ||
		searchParams.has(FILTER_URL_PARAMS.DATE_TIME_END)
	) {
		const start = parseCalendarDateRangeBound(
			searchParams.get(FILTER_URL_PARAMS.DATE_TIME_START),
			"start",
		);
		const end = parseCalendarDateRangeBound(
			searchParams.get(FILTER_URL_PARAMS.DATE_TIME_END),
			"end",
		);
		const dateRangeFilter =
			start && end && !isDateRangeInverted({ end, start })
				? { end, start }
				: null;
		updates.dateRangeFilter = dateRangeFilter;
	}

	if (searchParams.has(FILTER_URL_PARAMS.TYPES)) {
		updates.eventTypesFilter = parseCommaSeparated(
			searchParams.get(FILTER_URL_PARAMS.TYPES),
		).filter(isEventType);
	}

	if (searchParams.has(FILTER_URL_PARAMS.LOCATIONS)) {
		const raw = searchParams.get(FILTER_URL_PARAMS.LOCATIONS);
		if (raw !== null && raw.trim() !== "") {
			const parsed = parseCommaSeparated(raw);
			updates.locationsFilter = validateTreeIds
				? filterValidIds(parsed, locationIds)
				: parsed;
		}
	}

	if (searchParams.has(FILTER_URL_PARAMS.ZONES)) {
		const raw = searchParams.get(FILTER_URL_PARAMS.ZONES);
		if (raw !== null && raw.trim() !== "") {
			const parsed = parseCommaSeparated(raw);
			updates.zonesFilter = validateTreeIds
				? filterValidIds(parsed, zoneIds)
				: parsed;
		}
	}

	if (searchParams.has(FILTER_URL_PARAMS.SENSORS)) {
		const raw = searchParams.get(FILTER_URL_PARAMS.SENSORS);
		if (raw !== null && raw.trim() !== "") {
			const parsed = parseCommaSeparated(raw);
			updates.sensorsFilter = validateTreeIds
				? filterValidIds(parsed, sensorIds)
				: parsed;
		}
	}

	if (includeAlertStatus && searchParams.has(FILTER_URL_PARAMS.STATUS)) {
		const status = searchParams.get(FILTER_URL_PARAMS.STATUS);
		updates.statusFilter = status && isEventStatus(status) ? status : null;
	}

	return updates;
}
